import Button from '@ergeon/core-components/exports/Button.ts';
import Dialog from '@ergeon/core-components/exports/Dialog.ts';
import PopUp from '@ergeon/core-components/exports/PopUp.ts';
import { ExclamationTriangleIcon } from '@ergeon/icons/hero/solid';
import { AdjustmentsHorizontalIcon } from '@ergeon/icons/hero/mini';

import { Permissions } from '@/app/types';

interface DevicesRefusedDialogProps {
  requested: Permissions;
  isBlockedInSettings: boolean;
  isClient: boolean;
  onTryAgain(): void;
  onJoinWithout(): void;
  onStay(): void;
}

const deviceNames = ({ audio, video }: Permissions) =>
  [video && 'camera', audio && 'mic'].filter(Boolean).join(' and ');

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const consequence = ({ audio, video }: Permissions, isClient: boolean) => {
  const other = isClient ? 'Your sales rep' : 'The customer';
  if (audio && video) {
    return `${other} will neither see nor hear you.`;
  }
  if (audio) {
    return `${other} will not hear you.`;
  }
  if (isClient) {
    return `${other} will not see your property.`;
  }

  return `${other} will not see you.`;
};

export default function DevicesRefusedDialog({
  requested, isBlockedInSettings, isClient, onTryAgain, onJoinWithout, onStay,
}: DevicesRefusedDialogProps) {
  const devices = deviceNames(requested);
  const isPlural = Boolean(requested.audio && requested.video);

  const howToAllow = isBlockedInSettings ?
    (
      <ol className="flex list-inside list-decimal flex-col gap-2">
        <li>
          Tap the <AdjustmentsHorizontalIcon className="inline-block size-5 align-text-bottom" /> in your
          browser&apos;s address bar
        </li>
        <li>Allow the {devices}</li>
        <li>Reload the page</li>
      </ol>
    ) :
    (
      <p>Tap <b>Try again</b> and choose <b>Allow</b> when your browser asks.</p>
    );

  return (
    <PopUp isVisible maxWidth="small" onChangeVisible={onStay}>
      <Dialog
        icon={<ExclamationTriangleIcon className="size-6 text-orange-500 dark:text-orange-400" />}
        isVisible
        onDismiss={onStay}
        showButtons={false}
        title={`${capitalize(devices)} ${isPlural ? 'are' : 'is'} off`}>
        <div className="flex flex-col gap-4">
          <p>{consequence(requested, isClient)}</p>
          {howToAllow}
          <div className="flex flex-col gap-2">
            <Button flavor="primary" isFullWidth onClick={onTryAgain} size="large">
              Try again
            </Button>
            <Button
              flavor="regular" isFullWidth onClick={onJoinWithout} size="large"
              taste="line">
              Join without {devices}
            </Button>
          </div>
        </div>
      </Dialog>
    </PopUp>
  );
}
